import './App.scss';

import { AuthProvider, AuthProviderProps, User, useAuth } from 'oidc-react';
import React, { Suspense, useEffect } from 'react';

import { AuthEndpoint } from '@datarock/api-client';
import { CacheProvider } from '@data-client/react';
import { useBrowserId } from '@datarock/hooks';
import FullscreenLoader from './components/FullscreenLoader';
import { Router } from './routing';

const baseOidcConfig: Omit<AuthProviderProps, 'extraQueryParams'> = {
  onSignIn: (userData: User | null) => {
    AuthEndpoint.accessToken = userData?.access_token;
  },
  onSignOut: () => {
    AuthEndpoint.accessToken = undefined;
  },
  autoSignIn: false,
  authority: process.env.REACT_APP_IDENTITY_PROVIDER_URL,
  clientId: 'portal_client',
  scope: 'openid vengarapi',
  responseType: 'code',
  redirectUri: `${window.location.origin}/signin-callback`,
  postLogoutRedirectUri: `${window.location.origin}/signout-callback`
};

interface IAuthCheckProps {
  children: React.ReactNode;
}

function AuthCheck({ children }: IAuthCheckProps) {
  const auth = useAuth();

  const isExpired = auth.userData?.expired;

  if (auth.userData) {
    AuthEndpoint.accessToken = auth.userData?.access_token;
  }

  useEffect(() => {
    if (isExpired) {
      auth.userManager.signinRedirect();
    }
  }, [auth.userManager, isExpired]);

  return <>{children}</>;
}

function AuthProviderWithDevice() {
  const deviceId = useBrowserId();

  return (
    <AuthProvider {...baseOidcConfig} extraQueryParams={{ deviceid: deviceId }}>
      <AuthCheck>
        <Router />
      </AuthCheck>
    </AuthProvider>
  );
}

function App() {
  return (
    <CacheProvider>
      <Suspense fallback={<FullscreenLoader withLogo />}>
        <AuthProviderWithDevice />
      </Suspense>
    </CacheProvider>
  );
}

export default App;
